/** admin/sessions — pure helpers (session-script duration estimate, a session's timeline,
 * and whether a running session's match is gone). No I/O — kept out of api.ts since they
 * are not endpoints. */
import type {
  InterruptionCheck,
  RunningWorlds,
  SessionItem,
  SessionScriptStepSummary,
  TimelinePhase,
  TimelineStop,
} from './types';
import type { Game } from '../../moderator/moderation/types';
import { localDateInput, localTimeInput } from '../../../utils/date-input';
import {
  LOBBY_POST_MINUTES,
  LOBBY_PRE_MINUTES,
  SESSION_SCRIPT_FALLBACK_DURATION_MINUTES,
  SIM_INTERVAL_MINUTES,
  TICKS_PER_QUARTER,
} from '../../_shared/session-timing';

/** Whether a session is script-driven: it has no length of its own, and its script ends it. */
export function isScriptDriven(durationMinutes: number | null | undefined): boolean {
  return !durationMinutes || durationMinutes <= 0;
}

/**
 * How long a session runs, in minutes: its own length, or — script-driven —
 * the fallback, after which the game server ends its match and refuses a join
 * (its script may end it sooner). The sessions list's card and the session
 * form's preview both time a session by this, as the backend does under the
 * same name (session-window.ts), so the two say one end; the form says a
 * script's estimate beside it, as an estimate.
 */
export function sessionLengthMinutes(durationMinutes: number | null | undefined): number {
  return isScriptDriven(durationMinutes) ? SESSION_SCRIPT_FALLBACK_DURATION_MINUTES : (durationMinutes as number);
}

export function estimateSessionScriptDurationMinutes(steps: SessionScriptStepSummary[]): number | null {
  if (!steps || steps.length === 0) return null;
  const lastStep = steps[steps.length - 1];
  const end = lastStep.endTrigger;
  switch (end.type) {
    case 'quarter_end':
      if (end.quarter) return Math.ceil(end.quarter * TICKS_PER_QUARTER * SIM_INTERVAL_MINUTES);
      break;
    case 'match_time':
      if (end.seconds) return Math.ceil(end.seconds / 60);
      break;
    case 'elapsed': {
      let startMin = 0;
      if (lastStep.startTrigger.type === 'quarter_start' && lastStep.startTrigger.quarter) {
        startMin = (lastStep.startTrigger.quarter - 1) * TICKS_PER_QUARTER * SIM_INTERVAL_MINUTES;
      } else if (lastStep.startTrigger.type === 'match_time' && lastStep.startTrigger.seconds) {
        startMin = lastStep.startTrigger.seconds / 60;
      }
      if (end.seconds) return Math.ceil(startMin + end.seconds / 60);
      break;
    }
  }
  return null;
}

/**
 * A session's timeline, from its start (epoch seconds) and its length in
 * minutes: the lobby opens LOBBY_PRE_MINUTES before the start, play runs its
 * length, the end lobby LOBBY_POST_MINUTES, and then the room closes. Each
 * stop is at its local time, and dated only where its local day is not the
 * start's — the sessions list's card and the session form's preview both draw
 * it (SessionTimeline).
 */
export function sessionTimeline(startUtc: number, lengthMinutes: number): TimelineStop[] {
  const startMs = startUtc * 1000;
  const endMs = startMs + lengthMinutes * 60_000;
  const stops: [TimelinePhase, number][] = [
    ['startLobby', startMs - LOBBY_PRE_MINUTES * 60_000],
    ['playing', startMs],
    ['endLobby', endMs],
    ['closes', endMs + LOBBY_POST_MINUTES * 60_000],
  ];
  // The day and the time both local (date-input.ts). A date from toISOString()
  // beside a local time put the pair a day apart wherever the local and the
  // UTC day differ: at UTC-3 a 23:30 session read "2027-03-02 23:30" on the 1st.
  const startDay = localDateInput(new Date(startMs));
  return stops.map(([phase, ms]) => {
    const day = localDateInput(new Date(ms));
    return { phase, time: localTimeInput(new Date(ms)), otherDay: day === startDay ? null : day };
  });
}

/*
 * A running session whose match is gone.
 *
 * The portal marks a session RUNNING when the game server makes its match, and
 * FINISHED when the match reaches its end lobby. A match lost mid-session — the
 * game server restarted, or the match crashed — leaves its session RUNNING
 * with nothing to join: the players' ways in say so ("This session was
 * interrupted", session_match_lost), where the admin's sessions screen said "In
 * progress". The screen checks its running sessions against the matches the
 * game server runs — the list moderation reads — by their world, as the
 * server's join does: a session's players join any match running on its
 * world, so one Launch Match makes again carries the session on.
 */

const worldKey = (gameName: string, worldName: string) => `${gameName}/${worldName}`;

/**
 * The worlds the moderation list names a match on, under their games (a
 * game's id, a world's name, as the game server labels a match), read from
 * the list as moderationApi.listMatches parses it — which fails on an answer
 * that is not the list, so the check fails and says nothing, rather than
 * reading no matches and calling every running session interrupted.
 */
export function runningWorlds(games: readonly Game[]): RunningWorlds {
  return new Set(games.flatMap((game) => game.worlds.map((world) => worldKey(game.id, world.name))));
}

/**
 * The check after `before`, from sessions read before the matches were: a
 * session running then whose world the list names no match on is absent. (Read
 * the other way round, a match made between the two reads would be missing
 * from the list beside a session already running.)
 *
 * One reading proves nothing alone — the game server lists a match a second or
 * so after making it, so one just made, or made again by Launch Match, can be
 * missing from a reading — so a session is interrupted only when the check
 * before found it absent too, and that check is recent (`window` milliseconds
 * or less before), not one kept from minutes ago.
 */
export function nextInterruptionCheck(
  before: InterruptionCheck | undefined,
  sessions: readonly Pick<SessionItem, 'id' | 'status' | 'gameName' | 'worldName'>[],
  running: RunningWorlds,
  checkedAt: number,
  window: number,
): InterruptionCheck {
  const absent = new Set(
    sessions
      .filter((s) => s.status === 'RUNNING' && !running.has(worldKey(s.gameName, s.worldName)))
      .map((s) => s.id),
  );
  const earlier = before !== undefined && checkedAt - before.checkedAt <= window ? before.absent : new Set<string>();
  return { checkedAt, absent, interrupted: new Set([...absent].filter((id) => earlier.has(id))) };
}
